import { authenticationAppRoutes } from "@circle-react/components/OnboardingPage/helpers/routes";
import { useCheckoutSession } from "@circle-react/components/Paywalls/Checkout/contexts/CheckoutSessionContext";
import { updateSessionCache } from "@circle-react/components/Paywalls/Checkout/helpers/sessions/updateSessionCache";
import { getMainOffer } from "@circle-react/components/Paywalls/CheckoutWithSession/helpers/SessionStepQueryHelper";
import { useMutation, useQueryClient } from "react-query";

import { t } from "@/i18n-js/instance";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";
import { reactQueryPost } from "@/react/helpers/backendRequestHelpers";
import { setCsrfToken } from "@/react/helpers/csrfToken";
import { internalApi } from "@/react/helpers/urlHelpers";
import type { CheckoutSession } from "@/react/types/Paywalls/Checkout/Session";

import {
  EMAIL_VERIFICATION_ACTION_TYPES,
  type EmailVerificationAction,
  type EmailVerificationStatus,
  type ForgotPasswordResponse,
  type OTPResendResponse,
  type OTPVerificationResponse,
  type PasswordSignInResponse,
} from "../types";

export const extractErrorMessage = (error: any): string =>
  error?.message || t("paywall_checkout.form.errors.generic_error");

interface UseEmailVerificationMutationsProps {
  dispatch: React.Dispatch<EmailVerificationAction>;
}

export const useEmailVerificationMutations = ({
  dispatch,
}: UseEmailVerificationMutationsProps) => {
  const { success, error } = useToast();
  const { session } = useCheckoutSession();
  const queryClient = useQueryClient();

  const updateCachedSessionFromResponse = (
    checkoutSession: CheckoutSession,
  ) => {
    const mainOffer = getMainOffer(session?.steps[0]);
    if (!mainOffer) return;

    updateSessionCache(queryClient, mainOffer.paywall.id, checkoutSession);
  };

  const getSessionParams = () => {
    if (!session?.id) return {};
    const mainOffer = getMainOffer(session.steps[0]);
    if (!mainOffer) return {};
    return {
      checkout_session_id: session.id,
      paywall_id: mainOffer.paywall.id,
    };
  };

  const createErrorHandler =
    (errorActionType: EmailVerificationAction["type"]) =>
    (errorObject: any) => {
      const errorMessage = extractErrorMessage(errorObject);
      dispatch({
        type: errorActionType,
        payload: errorMessage,
      } as EmailVerificationAction);
      error(errorMessage, { duration: "long" });
    };

  const verifyEmailMutation = useMutation<
    { status: EmailVerificationStatus },
    Error,
    string
  >({
    mutationFn: (email: string) =>
      reactQueryPost(internalApi.paywallCheckout.emailVerifications.create(), {
        email,
      }),
  });

  const verifyOTPMutation = useMutation<
    OTPVerificationResponse,
    Error,
    { email: string; otp: string }
  >({
    mutationFn: ({ email, otp }) =>
      reactQueryPost(internalApi.paywallCheckout.otpSessions.create(), {
        user: { email, otp },
        ...getSessionParams(),
      }),
    onSuccess: (response, { otp }) => {
      dispatch({
        type: EMAIL_VERIFICATION_ACTION_TYPES.OTP_SUBMISSION_SUCCESS,
        payload: { otp },
      });
      if (response.checkout_session) {
        updateCachedSessionFromResponse(response.checkout_session);
      }
    },
    onError: createErrorHandler(
      EMAIL_VERIFICATION_ACTION_TYPES.OTP_SUBMISSION_ERROR,
    ),
  });

  const resendOTPMutation = useMutation<OTPResendResponse, Error, string>({
    mutationFn: (email: string) =>
      reactQueryPost(authenticationAppRoutes.resendOtp(), {
        user: { email },
      }),
    onSuccess: () => {
      dispatch({ type: EMAIL_VERIFICATION_ACTION_TYPES.RESEND_OTP_SUCCESS });
      success(t("paywall_checkout.form.resent_otp"));
    },
    onError: createErrorHandler(
      EMAIL_VERIFICATION_ACTION_TYPES.RESEND_OTP_ERROR,
    ),
  });

  const signInWithPasswordMutation = useMutation<
    PasswordSignInResponse,
    Error,
    { email: string; password: string }
  >({
    mutationFn: ({ email, password }) =>
      reactQueryPost(internalApi.paywallCheckout.authSessions.create(), {
        user: { email, password },
        ...getSessionParams(),
      }),
    onSuccess: response => {
      setCsrfToken(response.csrf_token);
      dispatch({
        type: EMAIL_VERIFICATION_ACTION_TYPES.PASSWORD_SUBMISSION_SUCCESS,
      });
      if (response.checkout_session) {
        updateCachedSessionFromResponse(response.checkout_session);
      }
    },
    onError: createErrorHandler(
      EMAIL_VERIFICATION_ACTION_TYPES.PASSWORD_SUBMISSION_ERROR,
    ),
  });

  const forgotPasswordMutation = useMutation<
    ForgotPasswordResponse,
    Error,
    string
  >({
    mutationFn: (email: string) =>
      reactQueryPost(authenticationAppRoutes.resetPassword(), {
        user: { email },
      }),
    onSuccess: () => {
      dispatch({
        type: EMAIL_VERIFICATION_ACTION_TYPES.FORGOT_PASSWORD_SUBMISSION_SUCCESS,
      });
      success(
        t(
          "paywall_checkout.form.email_verification_states.password_state.forgot_password.success",
        ),
      );
    },
    onError: createErrorHandler(
      EMAIL_VERIFICATION_ACTION_TYPES.FORGOT_PASSWORD_SUBMISSION_ERROR,
    ),
  });

  return {
    verifyEmailMutation,
    verifyOTPMutation,
    resendOTPMutation,
    signInWithPasswordMutation,
    forgotPasswordMutation,
  };
};
